import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { DatePickerProps, ViewMode, DateRange } from '@/shared/types/datepicker';
import { DatePickerState } from '../types';
import { InputRef } from '@/shared/types/input';
export const useDatePicker = (props: DatePickerProps) => {
  const {
    mode = 'single',
    selectedDate: externalSelectedDate = null,
    selectedRange: externalSelectedRange = { start: null, end: null },
    initialViewMode = 'days',
    closeOnSelect = false,
    autoCloseRange = true,
    disabled = false,
  } = props;

  const [state, setState] = useState<DatePickerState>({
    isOpen: false,
    currentDate: new Date(),
    viewMode: initialViewMode,
    internalDate: null,
    internalRange: { start: null, end: null },
    hoverDate: null,
    portalPosition: { top: 0, left: 0 },
    pendingDate: null,
    pendingRange: { start: null, end: null },
  });

  // Estado adicional para controlar si el portal está listo para renderizar
  const [isPortalReady, setIsPortalReady] = useState(false);

  const inputRef = useRef<InputRef>(null);

  // Ref para guardar el rango original cuando se abre el picker (para poder revertir con Cancelar)
  const originalRangeRef = useRef<DateRange | null>(null);
  // Ref para guardar la fecha original cuando se abre el picker (para poder revertir con Cancelar)
  const originalDateRef = useRef<Date | null>(null);

  // Manejo de estado controlado/no-controlado
  // Usamos props directamente para verificar si el componente es controlado
  // porque externalSelectedDate tiene un valor por defecto de null
  const isControlled =
    mode === 'single' ? props.selectedDate !== undefined : props.selectedRange !== undefined;

  const isUncontrolled =
    mode === 'single' ? props.selectedDate === undefined : props.selectedRange === undefined;

  // Validar y procesar selectedDate
  // Si closeOnSelect es false, usar pendingDate para la selección visual en el calendario
  // pero usar la fecha confirmada para el input
  const selectedDateForCalendar =
    mode === 'single'
      ? closeOnSelect === false && state.pendingDate !== null
        ? state.pendingDate
        : isUncontrolled
          ? state.internalDate
          : externalSelectedDate
      : null;

  const selectedDateForInput =
    mode === 'single' ? (isUncontrolled ? state.internalDate : externalSelectedDate) : null;

  // Validar y procesar selectedRange
  // Si autoCloseRange es false, usar pendingRange para la selección visual en el calendario
  // pero usar el rango confirmado para el input
  const selectedRangeForCalendar =
    mode === 'range'
      ? autoCloseRange === false && (state.pendingRange.start || state.pendingRange.end)
        ? state.pendingRange
        : isUncontrolled
          ? state.internalRange
          : externalSelectedRange
      : { start: null, end: null };

  const selectedRangeForInput =
    mode === 'range'
      ? isUncontrolled
        ? state.internalRange
        : externalSelectedRange
      : { start: null, end: null };

  // Función auxiliar para validar fechas
  const isValidDate = (date: unknown): date is Date => {
    return date !== null && date !== undefined && date instanceof Date && !isNaN(date.getTime());
  };

  /**
   * Normaliza una fecha a medianoche en hora local usando el día civil UTC.
   * Corrige el desfase cuando las fechas vienen de ISO (ej. 2026-02-28T00:00:00.000Z)
   * y el calendario pinta celdas en hora local (medianoche local).
   */
  const toLocalDateOnly = useCallback((d: Date): Date => {
    return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 0, 0, 0, 0);
  }, []);

  // Validar que selectedDate sea una fecha válida (para el calendario)
  const validatedSelectedDate = isValidDate(selectedDateForCalendar)
    ? selectedDateForCalendar
    : null;

  // Claves primitivas: `selectedRangeForCalendar` puede ser un objeto nuevo cada render (p. ej. `{ start: null, end: null }` en modo single).
  const calendarRangeStartMs = isValidDate(selectedRangeForCalendar?.start)
    ? selectedRangeForCalendar!.start.getTime()
    : null;
  const calendarRangeEndMs = isValidDate(selectedRangeForCalendar?.end)
    ? selectedRangeForCalendar!.end.getTime()
    : null;

  // Validar que selectedRange tenga fechas válidas (para el calendario).
  // Se normalizan a "solo fecha" en local para evitar desfase de un día con fechas ISO/UTC.
  const validatedSelectedRange = useMemo(
    () => {
      const start = isValidDate(selectedRangeForCalendar?.start)
        ? toLocalDateOnly(selectedRangeForCalendar!.start)
        : null;
      const end = isValidDate(selectedRangeForCalendar?.end)
        ? toLocalDateOnly(selectedRangeForCalendar!.end)
        : null;
      return { start, end };
    },
    // Objetos `selectedRangeForCalendar` pueden recrearse cada render; deps = timestamps reales.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intencional: ver comentario anterior
    [calendarRangeStartMs, calendarRangeEndMs, toLocalDateOnly]
  );

  // Validar fechas para el input (solo las confirmadas). Misma normalización para evitar desfase.
  const validatedSelectedDateForInput = isValidDate(selectedDateForInput)
    ? selectedDateForInput
    : null;

  const inputRangeStartMs = isValidDate(selectedRangeForInput?.start)
    ? selectedRangeForInput!.start.getTime()
    : null;
  const inputRangeEndMs = isValidDate(selectedRangeForInput?.end)
    ? selectedRangeForInput!.end.getTime()
    : null;

  const validatedSelectedRangeForInput = useMemo(
    () => ({
      start: isValidDate(selectedRangeForInput?.start)
        ? toLocalDateOnly(selectedRangeForInput!.start)
        : null,
      end: isValidDate(selectedRangeForInput?.end)
        ? toLocalDateOnly(selectedRangeForInput!.end)
        : null,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intencional: objeto rango inestable; deps = timestamps
    [inputRangeStartMs, inputRangeEndMs, toLocalDateOnly]
  );

  const openPicker = useCallback(() => {
    if (disabled) return;

    // Guardar el rango original cuando se abre el picker (para poder revertir con Cancelar)
    if (mode === 'range') {
      originalRangeRef.current = {
        start: validatedSelectedRangeForInput.start
          ? new Date(validatedSelectedRangeForInput.start)
          : null,
        end: validatedSelectedRangeForInput.end
          ? new Date(validatedSelectedRangeForInput.end)
          : null,
      };
      // Inicializar el rango pendiente con el rango confirmado actual
      setState((prev) => ({
        ...prev,
        pendingRange: {
          start: validatedSelectedRangeForInput.start
            ? new Date(validatedSelectedRangeForInput.start)
            : null,
          end: validatedSelectedRangeForInput.end
            ? new Date(validatedSelectedRangeForInput.end)
            : null,
        },
      }));
    }
    // Guardar la fecha original cuando se abre el picker (para poder revertir con Cancelar)
    if (mode === 'single') {
      originalDateRef.current = validatedSelectedDateForInput
        ? new Date(validatedSelectedDateForInput)
        : null;
      // Inicializar la fecha pendiente con la fecha confirmada actual
      setState((prev) => ({
        ...prev,
        pendingDate: validatedSelectedDateForInput ? new Date(validatedSelectedDateForInput) : null,
      }));
    }

    // Primero calcular la posición antes de abrir
    if (inputRef.current) {
      const inputElement = inputRef.current.getInputElement();
      if (inputElement) {
        const inputRect = inputElement.getBoundingClientRect();

        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;
        const portalHeight = 320;
        const portalWidth = 256;
        const margin = 8;

        let top: number;
        let left = inputRect.left;

        // Verificar si cabe abajo del input con margen
        const spaceBelow = viewportHeight - inputRect.bottom;
        const spaceAbove = inputRect.top;

        if (spaceBelow >= portalHeight + margin) {
          // Posicionar abajo del input con margen
          top = inputRect.bottom + margin;
        } else if (spaceAbove >= portalHeight + margin) {
          // Posicionar arriba del input con margen
          top = inputRect.top - portalHeight - margin;
        } else {
          // Si no hay espacio suficiente arriba ni abajo, posicionar donde quepa mejor
          if (spaceBelow > spaceAbove) {
            // Más espacio abajo, posicionar abajo aunque se oculte parcialmente
            top = inputRect.bottom + margin;
          } else {
            // Más espacio arriba, posicionar arriba aunque se oculte parcialmente
            top = inputRect.top - portalHeight - margin;
          }
        }

        // Ajustar posición horizontal si no cabe a la derecha
        if (inputRect.left + portalWidth > viewportWidth) {
          left = inputRect.right - portalWidth;
        }

        // Asegurar que no se salga de los límites del viewport horizontalmente
        left = Math.max(0, Math.min(left, viewportWidth - portalWidth));

        // Abrir con la posición calculada
        setState((prev) => ({
          ...prev,
          isOpen: true,
          portalPosition: { top, left },
        }));
        setIsPortalReady(true);
        return;
      }
    }

    // Fallback: abrir sin posición calculada (se actualizará en el useEffect)
    setState((prev) => ({ ...prev, isOpen: true }));
    setIsPortalReady(false);
  }, [disabled, mode, validatedSelectedDateForInput, validatedSelectedRangeForInput]);

  const closePicker = useCallback(() => {
    setState((prev) => ({ ...prev, isOpen: false }));
    setIsPortalReady(false);
  }, []);

  useEffect(() => {
    if (disabled) {
      closePicker();
    }
  }, [disabled, closePicker]);

  const restoreOriginalRange = useCallback(() => {
    // Restaurar el rango original cuando se presiona Cancelar
    if (mode === 'range' && originalRangeRef.current) {
      if (!isControlled) {
        // Si no está controlado, restaurar el estado interno
        setState((prev) => ({
          ...prev,
          internalRange: {
            start: originalRangeRef.current!.start
              ? new Date(originalRangeRef.current!.start)
              : null,
            end: originalRangeRef.current!.end ? new Date(originalRangeRef.current!.end) : null,
          },
        }));
      }
      // Si está controlado, no podemos restaurar porque el estado está controlado externamente
      // El componente padre debería manejar la restauración si es necesario
    }
  }, [mode, isControlled]);

  const restoreOriginalDate = useCallback(() => {
    // Restaurar la fecha original cuando se presiona Cancelar
    if (mode === 'single' && originalDateRef.current !== null) {
      if (!isControlled) {
        // Si no está controlado, restaurar el estado interno
        setState((prev) => ({
          ...prev,
          internalDate: originalDateRef.current ? new Date(originalDateRef.current) : null,
        }));
      }
      // Si está controlado, no podemos restaurar porque el estado está controlado externamente
      // El componente padre debería manejar la restauración si es necesario
    }
  }, [mode, isControlled]);

  const setPendingDate = useCallback((date: Date | null) => {
    setState((prev) => ({ ...prev, pendingDate: date }));
  }, []);

  const setPendingRange = useCallback((range: DateRange) => {
    setState((prev) => ({ ...prev, pendingRange: range }));
  }, []);

  const setCurrentDate = useCallback((date: Date) => {
    setState((prev) => ({ ...prev, currentDate: date }));
  }, []);

  const setViewMode = useCallback((mode: ViewMode) => {
    setState((prev) => ({ ...prev, viewMode: mode }));
  }, []);

  // Función de navegación que faltaba
  const navigate = useCallback((direction: 'prev' | 'next') => {
    setState((prev) => {
      const newDate = new Date(prev.currentDate);

      switch (prev.viewMode) {
        case 'days':
          newDate.setMonth(newDate.getMonth() + (direction === 'next' ? 1 : -1));
          break;
        case 'months':
          newDate.setFullYear(newDate.getFullYear() + (direction === 'next' ? 1 : -1));
          break;
        case 'years':
          newDate.setFullYear(newDate.getFullYear() + (direction === 'next' ? 12 : -12));
          break;
      }

      return { ...prev, currentDate: newDate };
    });
  }, []);

  const setSelectedDate = useCallback(
    (date: Date | null, confirm: boolean = false) => {
      if (mode === 'single') {
        if (closeOnSelect === true || confirm === true) {
          // Si closeOnSelect es true o se confirma explícitamente, actualizar el estado confirmado
          if (isUncontrolled) {
            setState((prev) => ({ ...prev, internalDate: date, pendingDate: null }));
          }
          // Llamar a onDateChange cuando se confirma (ya sea por closeOnSelect o por botón Aceptar)
          if (closeOnSelect === true || confirm === true) {
            props.onDateChange?.(date);
          }
          if (closeOnSelect === true) {
            closePicker();
          }
        } else {
          // Si closeOnSelect es false y no se confirma, solo actualizar el estado pendiente para la selección visual
          // No actualizar el estado confirmado ni el input hasta que se presione "Aceptar"
          setState((prev) => ({ ...prev, pendingDate: date }));
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- props.onDateChange en deps; evitar recrear en cada cambio de props
    [mode, isUncontrolled, props.onDateChange, closeOnSelect, closePicker]
  );

  const setSelectedRange = useCallback(
    (range: DateRange, confirm: boolean = false) => {
      if (mode === 'range') {
        if (autoCloseRange === true || confirm === true) {
          // Si autoCloseRange es true o se confirma explícitamente, actualizar el estado confirmado
          if (!isControlled) {
            setState((prev) => ({
              ...prev,
              internalRange: range,
              pendingRange: { start: null, end: null },
            }));
          }
          // Llamar a onRangeChange cuando se confirma (ya sea por autoCloseRange o por botón Aceptar)
          if (autoCloseRange === true || confirm === true) {
            props.onRangeChange?.(range);
          }
          if (autoCloseRange === true && range.start && range.end) {
            closePicker();
          }
        } else {
          // Si autoCloseRange es false y no se confirma, solo actualizar el estado pendiente para la selección visual
          // No actualizar el estado confirmado ni el input hasta que se presione "Aceptar"
          setState((prev) => ({ ...prev, pendingRange: range }));
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- props.onRangeChange en deps; evitar recrear en cada cambio de props
    [mode, isControlled, props.onRangeChange, autoCloseRange, closePicker]
  );

  const setHoverDate = useCallback((date: Date | null) => {
    setState((prev) => ({ ...prev, hoverDate: date }));
  }, []);

  // Función para manejar la selección de mes
  const handleMonthSelect = useCallback((monthIndex: number) => {
    setState((prev) => {
      const newDate = new Date(prev.currentDate);
      newDate.setMonth(monthIndex);
      return {
        ...prev,
        currentDate: newDate,
        viewMode: 'days',
      };
    });
  }, []);

  // Función para manejar la selección de año
  const handleYearSelect = useCallback((year: number) => {
    setState((prev) => {
      const newDate = new Date(prev.currentDate);
      newDate.setFullYear(year);
      return {
        ...prev,
        currentDate: newDate,
        viewMode: 'months',
      };
    });
  }, []);

  // Función para ir a la fecha de hoy
  const goToToday = useCallback(() => {
    const today = new Date();
    setState((prev) => ({
      ...prev,
      currentDate: today,
      viewMode: 'days',
    }));
  }, []);

  const updatePortalPosition = useCallback(() => {
    if (!inputRef.current) {
      return;
    }

    const inputElement = inputRef.current.getInputElement();
    if (!inputElement) {
      return;
    }

    const inputRect = inputElement.getBoundingClientRect();

    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const portalHeight = 320;
    const portalWidth = 256;
    const margin = 8;

    let top: number;
    let left = inputRect.left;

    // Verificar si cabe abajo del input con margen
    const spaceBelow = viewportHeight - inputRect.bottom;
    const spaceAbove = inputRect.top;

    if (spaceBelow >= portalHeight + margin) {
      // Posicionar abajo del input con margen
      top = inputRect.bottom + margin;
    } else if (spaceAbove >= portalHeight + margin) {
      // Posicionar arriba del input con margen
      top = inputRect.top - portalHeight - margin;
    } else {
      // Si no hay espacio suficiente arriba ni abajo, posicionar donde quepa mejor
      if (spaceBelow > spaceAbove) {
        // Más espacio abajo, posicionar abajo aunque se oculte parcialmente
        top = inputRect.bottom + margin;
      } else {
        // Más espacio arriba, posicionar arriba aunque se oculte parcialmente
        top = inputRect.top - portalHeight - margin;
      }
    }

    // Ajustar posición horizontal si no cabe a la derecha
    if (inputRect.left + portalWidth > viewportWidth) {
      left = inputRect.right - portalWidth;
    }

    // Asegurar que no se salga de los límites del viewport horizontalmente
    left = Math.max(0, Math.min(left, viewportWidth - portalWidth));

    // Permitir posicionamiento parcial fuera del viewport
    // Solo limitar horizontalmente para evitar que se salga por los lados
    setState((prev) => ({ ...prev, portalPosition: { top, left } }));
    setIsPortalReady(true);
  }, []);

  // Efectos para manejar scroll y resize
  useEffect(() => {
    if (!state.isOpen) return;

    const handleScroll = () => updatePortalPosition();
    const handleResize = () => updatePortalPosition();

    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [state.isOpen, updatePortalPosition]);

  useEffect(() => {
    if (state.isOpen) {
      // Usar un pequeño delay para asegurar que el DOM esté listo
      const timer = setTimeout(() => {
        updatePortalPosition();
      }, 0);

      return () => clearTimeout(timer);
    }
  }, [state.isOpen, updatePortalPosition]);

  return {
    state,
    selectedDate: validatedSelectedDate,
    selectedRange: validatedSelectedRange,
    selectedDateForInput: validatedSelectedDateForInput,
    selectedRangeForInput: validatedSelectedRangeForInput,
    inputRef,
    isPortalReady,
    actions: {
      openPicker,
      closePicker,
      setCurrentDate,
      setViewMode,
      setSelectedDate,
      setSelectedRange,
      setHoverDate,
      updatePortalPosition,
      navigate,
      handleMonthSelect,
      handleYearSelect,
      goToToday,
      restoreOriginalRange,
      restoreOriginalDate,
      setPendingDate,
      setPendingRange,
    },
  };
};
